/**
 * Pure span math for studio closures: how long one runs, how it reads, and
 * what removing it costs.
 *
 * Separate from lib/closures.ts on purpose. That module owns the Firestore
 * collection, so importing it drags in React and the Firebase SDK; this one
 * imports nothing, which keeps the span rules testable in a plain node --test
 * run with no browser and no app boot.
 *
 * Dates are 'YYYY-MM-DD' strings throughout, matching ClosureDoc. They compare
 * the same lexicographically as chronologically, so no timezone math is needed
 * for what is an all-day concept.
 */

/** The parts of a closure this math needs. ClosureDoc satisfies it. */
export interface ClosureSpan {
  /** Inclusive start, 'YYYY-MM-DD'. */
  startDate: string;
  /** Inclusive end, 'YYYY-MM-DD'. Equals startDate for a single day. */
  endDate: string;
  /** Human label, e.g. "Thanksgiving Break". */
  label: string;
}

/** What a Director types into the closure form, before it is checked. */
export interface ClosureInput {
  startDate: string;
  endDate: string;
  label: string;
}

/**
 * The rules a closure has to pass before it is written, shared by create and
 * update so the two can never drift apart. Throws with the sentence to show
 * the Director, since every one of these is something only they can fix.
 *
 * A blank "To" means a single day, matching the Closed days form, where the
 * ONE DAY span leaves the end date empty.
 */
export function normalizeClosureInput(input: ClosureInput): ClosureInput {
  const startDate = input.startDate;
  const endDate = input.endDate || input.startDate;
  const label = input.label.trim();
  if (!startDate) throw new Error('Pick a start date.');
  if (endDate < startDate) throw new Error('The end date is before the start date.');
  if (!label) throw new Error('Give the closure a name.');
  return { startDate, endDate, label };
}

/**
 * Inclusive day count for a closure span, so Nov 23 to Nov 29 is 7 and not 6.
 *
 * Both ends are anchored at noon rather than midnight: a range crossing a DST
 * boundary is 6.958 or 7.042 real days from midnight to midnight, and noon
 * keeps that away from the rounding line entirely.
 *
 * A backwards or unparseable span counts 0 rather than going negative, so a
 * caller rendering "N days" never prints something impossible.
 */
export function closureDayCount(startDate: string, endDate: string): number {
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  return days > 0 ? days : 0;
}

/** Format a closure's span for display, collapsing single days. */
export function formatClosureRange(c: ClosureSpan): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  return c.startDate === c.endDate
    ? fmt(c.startDate)
    : `${fmt(c.startDate)} to ${fmt(c.endDate)}`;
}

/**
 * The sentence shown before a closure is deleted.
 *
 * A closure is a RANGE, so the banner sitting on Monday belongs to a doc that
 * may also close the rest of the week. Removing it reopens every one of those
 * days, and this spells that out, so a snow day and a week-long break never
 * read the same at the moment of confirming.
 */
export function closureRemovalPrompt(c: ClosureSpan): string {
  const days = closureDayCount(c.startDate, c.endDate);
  const span = formatClosureRange(c);
  return days > 1
    ? `Remove "${c.label}"? That reopens all ${days} days, ${span}.`
    : `Remove "${c.label}"? The studio goes back to open on ${span}.`;
}
